"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { getCookie, setCookie } from "@/lib/cookies";

type Language = "en" | "id";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");

  useEffect(() => {
    // Read cookie immediately on mount and update state
    const savedLanguage = (getCookie("lang") as Language) || "en";
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLanguageState(savedLanguage);
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      setCookie("lang", lang, {
        maxAge: 60 * 60 * 24 * 365, // 1 year
        path: "/",
        secure: true,
        sameSite: "Lax",
      });
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  // Default to English if context is undefined (safe fallback)
  if (context === undefined) {
    return { language: "en" as Language, setLanguage: () => { } };
  }
  return context;
}
